import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, ServerError, TypedDocumentNode } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { AppConfig } from '../app.config';
import { getCustomerLanguage } from '../helpers/language/language.helper';
import { generateCorrelationId, setSessionId } from '../helpers/logging/logging-helper';
import { inIframe } from '../helpers';
import { getPublicDealerIdFromParams } from '../helpers/params/params';
import { signalRService } from '../services/signalRService';

export const sessionId = setSessionId();

const httpLink = new HttpLink({
  uri: AppConfig.edgeServiceUrl,
  credentials: 'include',
});

/**
 * The headers every GraphQL request carries, assembled fresh per operation so
 * anything that can change mid-session (the language toggle, a token arriving) is
 * picked up on the next request rather than frozen at client construction.
 *
 * Exported for the tests: which headers are present is a contract with LAS, and
 * X-Language in particular has to be absent rather than defaulted (see below).
 */
export const buildContextHeaders = (): Record<string, unknown> => {
  // Retrieve publicDealerId from query params (full page method)
  let publicDealerId = getPublicDealerIdFromParams();

  // if publicDealerId not in query params, attempt to grab it from session storage (modal method)
  if (!publicDealerId) {
    publicDealerId = sessionStorage.getItem('publicDealerId');
  }

  let headers: Record<string, unknown> = {
    'public-dealer-id': publicDealerId || ``,
    'Session-ID': sessionId || ``,
    'Correlation-ID': generateCorrelationId(),
    'x-in-iframe': inIframe(),
    'X-Browser-Tab-Concurrency-Id': signalRService.getBrowserTabConcurrencyId(),
  };

  // The customer's UI language (en / es), read at call time so language toggling
  // mid-session is reflected on subsequent requests. Touchpoints (draft app, submit,
  // acknowledge contract, sign lease) consume this header downstream: LAS stamps
  // dbo.Applications.ApplicationLanguage from it and tags activity logs with it.
  //
  // Omitted entirely while the app is only defaulting to English, rather than sent
  // as 'en'. LAS records what it is told, so a defaulted 'en' becomes an English
  // stamp indistinguishable from a customer who chose English, and it lands on the
  // first touchpoint of every Spanish customer whose browser is configured in
  // English. Sending no header leaves LAS's own "no language on the request" path to
  // skip the stamp, so the record stays empty until we actually know (OR-2886).
  const customerLanguage = getCustomerLanguage();
  if (customerLanguage) {
    headers = {
      ...headers,
      'X-Language': customerLanguage,
    };
  }

  // check if unifiedSecurityToken is set in session storage,
  // this is only needed as a work around for safari users using our modal as safari blocks 3rd party cookies,
  // this logic can be removed when safari implements the 'Partitioned' cookie attribute
  const unifiedSecurityToken = sessionStorage.getItem('unifiedSecurityToken');
  if (unifiedSecurityToken) {
    headers = {
      ...headers,
      'x-unified-security-token': unifiedSecurityToken,
    };
  }

  return headers;
};

const setContextHeaders = new ApolloLink((operation, forward) => {
  // Use the setContext method to set the HTTP headers.
  operation.setContext({
    headers: buildContextHeaders(),
  });

  // Call the next link in the middleware chain.
  return forward(operation);
});

// check if unifiedSecurityToken header is returned in response and set in session storage,
// this is only needed as a work around for safari users using our modal as safari blocks 3rd party cookies,
// this logic can be removed when safari implements the 'Partitioned' cookie attribute
const afterwareLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const {
      response: { headers },
    } = operation.getContext();

    if (headers) {
      const token = headers.get('x-unified-security-token');

      if (token) {
        if (token === 'expired') {
          sessionStorage.removeItem('unifiedSecurityToken');
        } else {
          sessionStorage.setItem('unifiedSecurityToken', token);
        }
      }
    }

    return response;
  });
});

// check if unifiedSecurityToken header is returned in response and clear session storage if token is set as 'delete',
// this is only needed as a work around for safari users using our modal as safari blocks 3rd party cookies,
// this logic can be removed when safari implements the 'Partitioned' cookie attribute
const clearTokenLink = onError(({ operation, networkError }) => {
  let networkErr: ServerError = networkError as ServerError;
  if (networkErr?.statusCode === 401 || networkErr?.statusCode === 403) {
    const {
      response: { headers },
    } = operation.getContext();
    const token = headers.get('x-unified-security-token');

    if (token === 'expired') {
      sessionStorage.removeItem('unifiedSecurityToken');
    }
  }
});

const client = new ApolloClient({
  link: clearTokenLink.concat(afterwareLink.concat(setContextHeaders.concat(httpLink))),
  cache: new InMemoryCache(),
  devtools: {
    enabled: true,
  },
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all', // tells the client to put graphql errors in there response.errors property instead of throwing them. http, networking, npe errors will still be thrown
    },
  },
});

export const apolloQuery = async (queryDoc: TypedDocumentNode, payload?: object) => {
  return client.query({
    query: queryDoc,
    variables: payload,
  });
};

// TODO: I think this return type should be Promise<FetchResult<Mutation>>
export const apolloMutation = async (mutationDoc: TypedDocumentNode, payload?: object, context?: object) => {
  // Want to generate correlation id here + send to api
  return client.mutate({
    mutation: mutationDoc,
    variables: payload,
    ...(context && context),
  });
};

export default client;
